import {
  createContent as publicCreateContent,
  getContent as publicGetContent,
  updateContent as publicUpdateContent,
  updateContentByKey as publicUpdateContentByKey,
  deleteContent as publicDeleteContent,
  queryContents as publicQueryContents,
  typedQueryContents as publicTypedQueryContents,
  searchContents as publicSearchContents,
  bulkCreateContent as publicBulkCreateContent,
  bulkUpdateContent as publicBulkUpdateContent,
  bulkUpdateContentByKey as publicBulkUpdateContentByKey,
  bulkDeleteContent as publicBulkDeleteContent,
  getContentWordCount as publicGetContentWordCount,
} from './multilingual-translation-v1-content-translation-contents.public.js';
import { createRESTModule } from '@wix/sdk-runtime/rest-modules';
import { createEventModule } from '@wix/sdk-runtime/event-definition-modules';
import {
  BuildRESTFunction,
  MaybeContext,
  BuildEventDefinition,
} from '@wix/sdk-types';
import { HttpClient } from '@wix/sdk-types';
import { createQueryOverloadRouter } from '@wix/sdk-runtime/query-method-router';
import {
  ContentQuery,
  ContentsQueryBuilder,
  QueryContentsOptions,
  typedQueryContents as universalTypedQueryContents,
} from './multilingual-translation-v1-content-translation-contents.universal.js';
import { onContentCreated as publicOnContentCreated } from './multilingual-translation-v1-content-translation-contents.public.js';
import { onContentDeleted as publicOnContentDeleted } from './multilingual-translation-v1-content-translation-contents.public.js';
import { onContentUpdated as publicOnContentUpdated } from './multilingual-translation-v1-content-translation-contents.public.js';

function customQueryContents(httpClient: HttpClient) {
  const router = createQueryOverloadRouter({
    builderQueryFunction: (options?: QueryContentsOptions) =>
      publicQueryContents(httpClient)(options),
    typedQueryFunction: (query: ContentQuery, options?: QueryContentsOptions) =>
      publicTypedQueryContents(httpClient)(query, options),
    hasOptionsParameter: true,
  });

  function overloadedQuery(
    query: ContentQuery,
    options?: QueryContentsOptions
  ): ReturnType<typeof universalTypedQueryContents>;
  function overloadedQuery(
    options?: QueryContentsOptions
  ): ContentsQueryBuilder;
  function overloadedQuery(
    queryOrOptions?: ContentQuery | QueryContentsOptions,
    options?: QueryContentsOptions
  ): any {
    return router(...arguments);
  }

  return overloadedQuery;
}

export const createContent: MaybeContext<
  BuildRESTFunction<typeof publicCreateContent> & typeof publicCreateContent
> = /*#__PURE__*/ createRESTModule(publicCreateContent);
export const getContent: MaybeContext<
  BuildRESTFunction<typeof publicGetContent> & typeof publicGetContent
> = /*#__PURE__*/ createRESTModule(publicGetContent);
export const updateContent: MaybeContext<
  BuildRESTFunction<typeof publicUpdateContent> & typeof publicUpdateContent
> = /*#__PURE__*/ createRESTModule(publicUpdateContent);
export const updateContentByKey: MaybeContext<
  BuildRESTFunction<typeof publicUpdateContentByKey> &
    typeof publicUpdateContentByKey
> = /*#__PURE__*/ createRESTModule(publicUpdateContentByKey);
export const deleteContent: MaybeContext<
  BuildRESTFunction<typeof publicDeleteContent> & typeof publicDeleteContent
> = /*#__PURE__*/ createRESTModule(publicDeleteContent);
export const searchContents: MaybeContext<
  BuildRESTFunction<typeof publicSearchContents> & typeof publicSearchContents
> = /*#__PURE__*/ createRESTModule(publicSearchContents);
export const bulkCreateContent: MaybeContext<
  BuildRESTFunction<typeof publicBulkCreateContent> &
    typeof publicBulkCreateContent
> = /*#__PURE__*/ createRESTModule(publicBulkCreateContent);
export const bulkUpdateContent: MaybeContext<
  BuildRESTFunction<typeof publicBulkUpdateContent> &
    typeof publicBulkUpdateContent
> = /*#__PURE__*/ createRESTModule(publicBulkUpdateContent);
export const bulkUpdateContentByKey: MaybeContext<
  BuildRESTFunction<typeof publicBulkUpdateContentByKey> &
    typeof publicBulkUpdateContentByKey
> = /*#__PURE__*/ createRESTModule(publicBulkUpdateContentByKey);
export const bulkDeleteContent: MaybeContext<
  BuildRESTFunction<typeof publicBulkDeleteContent> &
    typeof publicBulkDeleteContent
> = /*#__PURE__*/ createRESTModule(publicBulkDeleteContent);
export const getContentWordCount: MaybeContext<
  BuildRESTFunction<typeof publicGetContentWordCount> &
    typeof publicGetContentWordCount
> = /*#__PURE__*/ createRESTModule(publicGetContentWordCount);
export const queryContents: MaybeContext<
  BuildRESTFunction<typeof customQueryContents> & typeof customQueryContents
> = /*#__PURE__*/ createRESTModule(customQueryContents);
/**
 * Triggered when translation content is created.
 */
export const onContentCreated: BuildEventDefinition<
  typeof publicOnContentCreated
> &
  typeof publicOnContentCreated = createEventModule(publicOnContentCreated);
/**
 * Triggered when translation content is deleted.
 */
export const onContentDeleted: BuildEventDefinition<
  typeof publicOnContentDeleted
> &
  typeof publicOnContentDeleted = createEventModule(publicOnContentDeleted);
/**
 * Triggered when translation content is updated.
 */
export const onContentUpdated: BuildEventDefinition<
  typeof publicOnContentUpdated
> &
  typeof publicOnContentUpdated = createEventModule(publicOnContentUpdated);

export {
  NodeType,
  BackgroundType,
  GradientType,
  WidthType,
  PluginContainerDataAlignment,
  ButtonDataType,
  Target,
  TextAlignment,
  LineStyle,
  Width,
  DividerDataAlignment,
  ViewMode,
  LayoutType,
  Orientation,
  Crop,
  ThumbnailsAlignment,
  GIFType,
  Source,
  StylesPosition,
  MapType,
  ViewRole,
  VoteRole,
  PollLayoutType,
  PollLayoutDirection,
  PollDesignBackgroundType,
  DecorationType,
  FontType,
  Position,
  AspectRatio,
  Resizing,
  Placement,
  Type,
  Alignment,
  Layout,
  AppType,
  InitialExpandedItems,
  Direction,
  VerticalAlignment,
  NullValue,
  ImageScalingScaling,
  ImagePosition,
  Origin,
  LayoutDataBackgroundType,
  BackdropType,
  VerticalAlignmentAlignment,
  ResponsivenessBehaviour,
  DesignTarget,
  Scaling,
  ImagePositionPosition,
  CardDataBackgroundType,
  ListStyle,
  Indentation,
  UpdaterIdentity,
  PublishStatus,
  SortOrder,
  SortType,
  SortDirection,
  MissingValues,
  ScalarType,
  NestedAggregationType,
  Interval,
  AggregationType,
  Mode,
  State,
  SiteCreatedContext,
  Namespace,
  DeleteStatus,
  SchemaScope,
  FieldType,
  WebhookIdentityType,
} from './multilingual-translation-v1-content-translation-contents.universal.js';
export {
  Content,
  ContentField,
  ContentFieldValueOneOf,
  RichContent,
  Node,
  NodeDataOneOf,
  NodeStyle,
  ButtonData,
  Gradient,
  Stop,
  Border,
  Colors,
  Background,
  PluginContainerData,
  PluginContainerDataWidth,
  PluginContainerDataWidthDataOneOf,
  Spoiler,
  Height,
  Styles,
  Link,
  LinkDataOneOf,
  Rel,
  CodeBlockData,
  TextStyle,
  DividerData,
  FileData,
  FileSource,
  FileSourceDataOneOf,
  PDFSettings,
  GalleryData,
  Media,
  Image,
  Video,
  Item,
  ItemDataOneOf,
  GalleryOptions,
  GalleryOptionsLayout,
  ItemStyle,
  Thumbnails,
  GIFData,
  GIF,
  HeadingData,
  HTMLData,
  HTMLDataDataOneOf,
  ImageData,
  StylesBorder,
  ImageDataStyles,
  LinkPreviewData,
  LinkPreviewDataStyles,
  MapData,
  MapSettings,
  ParagraphData,
  PollData,
  Permissions,
  Option,
  Settings,
  PollLayout,
  OptionLayout,
  BackgroundGradient,
  PollDesignBackground,
  PollDesignBackgroundBackgroundOneOf,
  PollDesign,
  OptionDesign,
  Poll,
  PollDataLayout,
  Design,
  TextData,
  Decoration,
  DecorationDataOneOf,
  AnchorData,
  ColorData,
  LinkData,
  MentionData,
  FontSizeData,
  SpoilerData,
  FontFamilyData,
  AppEmbedData,
  AppEmbedDataAppDataOneOf,
  BookingData,
  EventData,
  ButtonStyles,
  ImageStyles,
  RibbonStyles,
  CardStyles,
  PricingData,
  VideoData,
  PlaybackOptions,
  EmbedData,
  Oembed,
  CollapsibleListData,
  TableData,
  Dimensions,
  TableCellData,
  CellStyle,
  BorderColors,
  BorderWidths,
  ListValue,
  AudioData,
  OrderedListData,
  BulletedListData,
  BlockquoteData,
  CaptionData,
  LayoutData,
  LayoutDataBackgroundImage,
  Banner,
  LayoutDataBackground,
  Backdrop,
  LayoutCellData,
  ShapeData,
  ShapeDataStyles,
  CardData,
  CardDataBackground,
  BackgroundImage,
  TocData,
  Metadata,
  DocumentStyle,
  TextNodeStyle,
  VideoResolution,
  CreateContentRequest,
  CreateContentResponse,
  GetContentRequest,
  GetContentResponse,
  UpdateContentRequest,
  UpdateContentResponse,
  UpdateContentByKeyRequest,
  UpdateContentByKeyResponse,
  DeleteContentRequest,
  DeleteContentResponse,
  QueryContentsRequest,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Sorting,
  CursorPaging,
  QueryContentsResponse,
  CursorPagingMetadata,
  Cursors,
  QueryContentsLegacyRequest,
  QueryV2,
  QueryV2PagingMethodOneOf,
  Paging,
  QueryContentsLegacyResponse,
  PagingMetadataV2,
  SearchContentsRequest,
  CursorSearch,
  CursorSearchPagingMethodOneOf,
  Aggregation,
  AggregationKindOneOf,
  RangeBucket,
  IncludeMissingValuesOptions,
  ValueAggregation,
  ValueAggregationOptionsOneOf,
  RangeAggregation,
  ScalarAggregation,
  DateHistogramAggregation,
  NestedAggregationItem,
  NestedAggregationItemKindOneOf,
  NestedAggregation,
  GroupByAggregation,
  GroupByAggregationKindOneOf,
  SearchDetails,
  SearchContentsResponse,
  AggregationData,
  ValueAggregationResult,
  RangeAggregationResult,
  NestedAggregationResults,
  NestedAggregationResultsResultOneOf,
  ValueResults,
  RangeResults,
  AggregationResultsScalarResult,
  NestedValueAggregationResult,
  ValueResult,
  RangeResult,
  ScalarResult,
  NestedResultValue,
  NestedResultValueResultOneOf,
  Results,
  DateHistogramResult,
  GroupByValueResults,
  DateHistogramResults,
  NestedResults,
  AggregationResults,
  AggregationResultsResultOneOf,
  BulkCreateContentRequest,
  BulkCreateContentResponse,
  ItemMetadata,
  ApplicationError,
  BulkContentResult,
  BulkActionMetadata,
  BulkUpdateContentRequest,
  BulkUpdateContentRequestMaskedContent,
  BulkUpdateContentResponse,
  BulkUpdateContentResponseBulkContentResult,
  BulkUpdateContentByKeyRequest,
  MaskedContent,
  BulkUpdateContentByKeyResponse,
  BulkUpdateContentByKeyResponseBulkContentResult,
  PermissiveBulkUpdateContentRequest,
  PermissiveBulkUpdateContentRequestMaskedContent,
  PermissiveBulkUpdateContentResponse,
  PermissiveBulkUpdateContentResponseBulkContentResult,
  BulkDeleteContentRequest,
  BulkDeleteContentResponse,
  BulkDeleteContentResponseBulkContentResult,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  Asset,
  SiteCreated,
  SiteTransferred,
  SiteDeleted,
  DeleteContext,
  SiteUndeleted,
  SitePublished,
  SiteUnpublished,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  ServiceProvisioned,
  ServiceRemoved,
  SiteRenamed,
  SiteHardDeleted,
  NamespaceChanged,
  StudioAssigned,
  StudioUnassigned,
  SiteUrlChanged,
  SitePurgedExternally,
  OdeditorAssigned,
  OdeditorUnassigned,
  PicassoAssigned,
  PicassoUnassigned,
  WixelAssigned,
  WixelUnassigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  UserDomainMediaEnabled,
  UserDomainMediaDisabled,
  EditorlessAssigned,
  EditorlessUnassigned,
  Empty,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  RestoreInfo,
  EntityUpdatedEvent,
  EntityDeletedEvent,
  ActionEvent,
  UpdateSchemaCacheRequest,
  Schema,
  SchemaKey,
  SchemaField,
  PreviewFields,
  UpdateSchemaCacheResponse,
  RemoveContentsByFilterRequest,
  RemoveContentsByFilterResponse,
  RepublishContentByFilterRequest,
  RepublishContentByFilterResponse,
  ChangeContentLocaleRequest,
  ChangeContentLocaleRequestQueryContextOneOf,
  ChangeContentLocaleResponse,
  RestoreFromTrashBinByQueryRequest,
  RestoreFromTrashBinByQueryResponse,
  RestoreFromTrashBinByQueryResponseBulkContentResult,
  GetContentWordCountRequest,
  GetContentWordCountResponse,
  SchemaWordCount,
  MessageEnvelope,
  IdentificationData,
  IdentificationDataIdOneOf,
  AccountInfo,
  BaseEventMetadata,
  EventMetadata,
  AccountInfoMetadata,
  ContentCreatedEnvelope,
  ContentDeletedEnvelope,
  ContentUpdatedEnvelope,
  UpdateContent,
  UpdateContentOptions,
  UpdateContentByKeyOptions,
  QueryContentsOptions,
  ContentsQueryResult,
  ContentsQueryBuilder,
  ContentQuerySpec,
  ContentSearchSpec,
  BulkCreateContentOptions,
  BulkUpdateContentOptions,
  BulkUpdateContentByKeyOptions,
  GetContentWordCountOptions,
} from './multilingual-translation-v1-content-translation-contents.universal.js';
export { utils } from './multilingual-translation-v1-content-translation-contents.universal.js';
export {
  NodeTypeWithLiterals,
  BackgroundTypeWithLiterals,
  GradientTypeWithLiterals,
  WidthTypeWithLiterals,
  PluginContainerDataAlignmentWithLiterals,
  ButtonDataTypeWithLiterals,
  TargetWithLiterals,
  TextAlignmentWithLiterals,
  LineStyleWithLiterals,
  WidthWithLiterals,
  DividerDataAlignmentWithLiterals,
  ViewModeWithLiterals,
  LayoutTypeWithLiterals,
  OrientationWithLiterals,
  CropWithLiterals,
  ThumbnailsAlignmentWithLiterals,
  GIFTypeWithLiterals,
  SourceWithLiterals,
  StylesPositionWithLiterals,
  MapTypeWithLiterals,
  ViewRoleWithLiterals,
  VoteRoleWithLiterals,
  PollLayoutTypeWithLiterals,
  PollLayoutDirectionWithLiterals,
  PollDesignBackgroundTypeWithLiterals,
  DecorationTypeWithLiterals,
  FontTypeWithLiterals,
  PositionWithLiterals,
  AspectRatioWithLiterals,
  ResizingWithLiterals,
  PlacementWithLiterals,
  TypeWithLiterals,
  AlignmentWithLiterals,
  LayoutWithLiterals,
  AppTypeWithLiterals,
  InitialExpandedItemsWithLiterals,
  DirectionWithLiterals,
  VerticalAlignmentWithLiterals,
  NullValueWithLiterals,
  ImageScalingScalingWithLiterals,
  ImagePositionWithLiterals,
  OriginWithLiterals,
  LayoutDataBackgroundTypeWithLiterals,
  BackdropTypeWithLiterals,
  VerticalAlignmentAlignmentWithLiterals,
  ResponsivenessBehaviourWithLiterals,
  DesignTargetWithLiterals,
  ScalingWithLiterals,
  ImagePositionPositionWithLiterals,
  CardDataBackgroundTypeWithLiterals,
  ListStyleWithLiterals,
  IndentationWithLiterals,
  UpdaterIdentityWithLiterals,
  PublishStatusWithLiterals,
  SortOrderWithLiterals,
  SortTypeWithLiterals,
  SortDirectionWithLiterals,
  MissingValuesWithLiterals,
  ScalarTypeWithLiterals,
  NestedAggregationTypeWithLiterals,
  IntervalWithLiterals,
  AggregationTypeWithLiterals,
  ModeWithLiterals,
  StateWithLiterals,
  SiteCreatedContextWithLiterals,
  NamespaceWithLiterals,
  DeleteStatusWithLiterals,
  SchemaScopeWithLiterals,
  FieldTypeWithLiterals,
  WebhookIdentityTypeWithLiterals,
  CommonQueryWithEntityContext,
  ContentQuery,
  CommonSearchWithEntityContext,
  ContentSearch,
} from './multilingual-translation-v1-content-translation-contents.universal.js';
